/** @module others/renderSortMenu */ /* global rmlOpts */ import { _ as _ts_decorate } from "@swc/helpers/_/_ts_decorate";
import { _ as _ts_metadata } from "@swc/helpers/_/_ts_metadata";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import $ from "jquery";
import { Provider, inject, observer } from "mobx-react";
import { getSnapshot } from "mobx-state-tree";
import React from "react";
import { Menu, message } from "@devowl-wp/react-folder-tree";
import FolderBox, { FolderBoxHeader } from "../components/FolderBox.js";
import { FolderSelector } from "../components/FolderSelector.js";
import { DashIcon, Modal } from "../components/index.js";
import { BasicStore, createUnorganizedNode } from "../store/index.js";
import { IS_DARKMODE, getMediaDialogNextZIndex, hooks, i18n, textDots } from "../util/index.js";
const { Item, SubMenu, Divider, ItemGroup } = Menu;
if (process.env.PLUGIN_CTX === "pro") {
    /* onlypro:start */ hooks.register("folder/children/order/name_asc", (children, setChildren)=>{
        setChildren(children.sort((a, b)=>a.title.localeCompare(b.title)));
    }).register("folder/children/order/name_desc", (children, setChildren)=>{
        setChildren(children.sort((a, b)=>a.title.localeCompare(b.title) * -1));
    }).register("folder/children/order/id_asc folder/children/order/original", (children, setChildren)=>{
        setChildren(children.sort((a, b)=>a.id > b.id ? 1 : b.id > a.id ? -1 : 0));
    }).register("folder/children/order/id_desc", (children, setChildren)=>{
        setChildren(children.sort((a, b)=>a.id > b.id ? -1 : b.id > a.id ? 1 : 0));
    });
/* onlypro:end */ }
/**
 * An element rendering sortables for the popup menu.
 *
 * @returns React.Element[]
 */ const createSortables = (sortables, select, selectText)=>Object.keys(sortables).map((key)=>/*#__PURE__*/ _jsxs(Item, {
            children: [
                sortables[key],
                " ",
                select === key && /*#__PURE__*/ _jsxs("strong", {
                    children: [
                        "(",
                        selectText,
                        ")"
                    ]
                })
            ]
        }, key));
/**
 * Apply an order to a tree node object.
 *
 * @this AppTree
 */ async function applyOrder(selected, key, automatically) {
    if (!rmlOpts.others.isPro && rmlOpts.others.showProHints) {
        this.setState({
            showProFeature: "order-subfolders"
        });
    } else {
        const hide = message.loading(i18n("sortLoadingText", {
            name: selected.title
        }));
        await selected.applyChildrenOrder(key, automatically);
        hide();
    }
}
/**
 * When clicking on a menu item in the order menu popup.
 *
 * @this AppTree
 */ async function handleClick({ key, keyPath }) {
    const path = keyPath.reverse();
    const { selectedId, selected } = this.props.store;
    const _applyOrder = applyOrder.bind(this);
    if (path[0] === "manual") {
        this.setState({
            rearrangeBoxId: selectedId
        });
    } else if (path[0] === "applyOnce") {
        _applyOrder(selected, key);
    } else if (path[0] === "applyAutomatically") {
        _applyOrder(selected, key, true);
    } else if (key === "reset") {
        _applyOrder(selected, "original");
    } else if (key === "resetAutomatically") {
        _applyOrder(selected, "deactivate");
    } else if (key === "applyReindex") {
        _applyOrder(selected, "reindex");
    }
}
/**
 * Render the order menu.
 *
 * @type React.Element
 */ export default function() {
    const { store } = this.props;
    const { selectedId, selected } = store;
    const sortables = store.treeSortables;
    const isFolder = selectedId > 0;
    return isFolder ? /*#__PURE__*/ _jsxs(Menu, {
        onClick: handleClick.bind(this),
        theme: IS_DARKMODE ? "dark" : "light",
        children: [
            /*#__PURE__*/ _jsx(Item, {
                children: i18n("sortByManual", {
                    name: textDots(selected.title)
                })
            }, "manual"),
            /*#__PURE__*/ _jsx(Divider, {}),
            /*#__PURE__*/ _jsxs(ItemGroup, {
                title: /*#__PURE__*/ _jsxs("span", {
                    children: [
                        i18n("subfolders"),
                        " (",
                        textDots(selected.title),
                        ")"
                    ]
                }),
                children: [
                    !!selected.lastSubOrderBy && /*#__PURE__*/ _jsx(Item, {
                        children: i18n("resetOrder")
                    }, "reset"),
                    !selected.subOrderAutomatically ? /*#__PURE__*/ _jsx(SubMenu, {
                        title: i18n("applyOrderOnce"),
                        children: sortables && createSortables(sortables, selected.lastSubOrderBy, i18n("last"))
                    }, "applyOnce") : /*#__PURE__*/ _jsx(Item, {
                        children: i18n("deactivateOrderAutomatically")
                    }, "resetAutomatically"),
                    /*#__PURE__*/ _jsx(SubMenu, {
                        title: i18n("applyOrderAutomatically"),
                        children: sortables && createSortables(sortables, selected.subOrderAutomatically && selected.lastSubOrderBy, i18n("latest"))
                    }, "applyAutomatically"),
                    !!selected.lastSubOrderBy && /*#__PURE__*/ _jsx(Item, {
                        children: i18n("reindexOrder")
                    }, "applyReindex")
                ]
            })
        ]
    }) : /*#__PURE__*/ _jsx("span", {});
}
/**
 * Show a rearrange box for the selected folder id.
 *
 * @property {int} id The id of the folder
 * @extends React.Component
 */ class RearrangeBox extends React.Component {
    constructor(props){
        super(props), /**
     * Create the sibling tree.
     */ this.handleSelect = (parent)=>{
            const { siblingStore } = this;
            const tree = $.extend(true, [], getSnapshot(parent ? parent.childNodes : this.props.store.tree));
            tree.forEach((n)=>n.childNodes = []);
            siblingStore.setTree(tree);
            const { selected } = siblingStore;
            selected && selected.setter((n)=>n.selected = false); // Reset selection
        }, this.handleSave = async ()=>{
            this.setState({
                loading: true
            });
            try {
                const { store } = this.props;
                const { id } = this.item;
                const { parentStore, siblingStore } = this;
                const oldItem = store.getTreeItemById(id);
                const props = {
                    id,
                    oldIndex: oldItem.parentArray.indexOf(oldItem),
                    parentFromId: oldItem.parentId,
                    parentToId: parentStore.selected ? parentStore.selectedId : store.rootId,
                    nextId: siblingStore.selectedId || NaN,
                    doFinally: false
                };
                // Get newIndex
                const parentTo = store.getTreeItemById(props.parentToId);
                const newIndexRelative = parentTo ? parentTo.childNodes : store.tree;
                const newIndexA = newIndexRelative.filter(({ id })=>id !== oldItem.id).map(({ id }, idx)=>id === props.nextId ? `${idx}` : null).filter(Boolean).map((i)=>+i);
                props.newIndex = !isNaN(props.nextId) && newIndexA.length ? newIndexA[0] : newIndexRelative.length;
                // Do the sorting
                this.props.onSort(props);
            } finally{
                this.setState({
                    loading: false
                });
            }
        }, /**
     * Save the editing item to the instance and prepare the first sibling tree.
     */ this.handleNodeInit = (itemId)=>{
            const { nextSibling, parentArray } = this.item = this.parentStore.getTreeItemById(itemId, false);
            const id = nextSibling ? nextSibling.id : undefined;
            this.setState({
                nextId: id
            }, ()=>{
                const tree = $.extend(true, [], getSnapshot(parentArray));
                const { siblingStore, parentStore } = this;
                tree.forEach((n)=>n.childNodes = []);
                siblingStore.setTree(tree);
                parentStore.getTreeItemById(this.item.id).setter((n)=>n.$visible = false);
                siblingStore.getTreeItemById(this.item.id).setter((n)=>n.$visible = false);
                id !== undefined && siblingStore.getTreeItemById(id).setter((n)=>n.selected = true);
            });
        };
        this.state = {
            nextId: undefined,
            loading: false
        };
        this.parentStore = BasicStore.create({
            staticTree: [
                createUnorganizedNode()
            ]
        });
        // Create sibling store
        this.siblingStore = BasicStore.create({
            staticTree: [
                createUnorganizedNode()
            ]
        });
    }
    render() {
        let modalContent;
        let title;
        const selected = this.props.id && this.props.store.getTreeItemById(this.props.id, false);
        if (selected) {
            const selectedId = selected.parentId > 0 ? selected.parentId : "";
            title = /*#__PURE__*/ _jsx(FolderBoxHeader, {
                icon: /*#__PURE__*/ _jsx(DashIcon, {
                    name: "sort"
                }),
                folder: selected
            });
            modalContent = /*#__PURE__*/ _jsx(FolderBox, {
                folder: selected,
                header: false,
                children: /*#__PURE__*/ _jsxs("form", {
                    children: [
                        /*#__PURE__*/ _jsx("h3", {
                            children: i18n("parent")
                        }),
                        /*#__PURE__*/ _jsx(Provider, {
                            tree: this.parentStore,
                            children: /*#__PURE__*/ _jsx(FolderSelector, {
                                selected: selectedId,
                                nullable: true,
                                disabled: "4",
                                onSelect: this.handleSelect,
                                onNodeInit: (...args)=>this.handleNodeInit(this.props.id, ...args)
                            })
                        }),
                        /*#__PURE__*/ _jsx("h3", {
                            children: i18n("beforeThisNode")
                        }),
                        /*#__PURE__*/ _jsx(Provider, {
                            tree: this.siblingStore,
                            children: /*#__PURE__*/ _jsx(FolderSelector, {
                                fetch: false,
                                nullable: true,
                                disabled: "4",
                                ignoreChildNodes: true
                            })
                        }),
                        /*#__PURE__*/ _jsx("p", {
                            className: "description",
                            children: i18n("beforeThisNodeInfo")
                        })
                    ]
                })
            });
        }
        return /*#__PURE__*/ _jsx(Modal, {
            visible: !!modalContent,
            title: title,
            onOk: this.handleSave,
            onCancel: this.props.onClose,
            cancelText: i18n("cancel"),
            okText: i18n("save"),
            zIndex: getMediaDialogNextZIndex(),
            confirmLoading: this.state.loading,
            children: modalContent
        });
    }
}
RearrangeBox = _ts_decorate([
    inject("store"),
    observer,
    _ts_metadata("design:type", Function),
    _ts_metadata("design:paramtypes", [
        void 0
    ])
], RearrangeBox);
export { RearrangeBox };
